import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useVenue } from '../../contexts/VenueContext';
import { fetchSelectedVenueApplication, saveSelectedVenueApplication } from '../../utils/venueAppApi';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { VenueSetupBackButton } from '../../components/venue/VenueSetupBackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

export default function VenueSocialMediaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeVenueAppId } = useVenue();
  const [links, setLinks] = useState({ website: '', instagram: '', facebook: '', tiktok: '' });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);
  const latestLinksRef = useRef({ website: '', instagram: '', facebook: '', tiktok: '' });

  useEffect(() => {
    const fetch = async () => {
      try {
        const app = await fetchSelectedVenueApplication(activeVenueAppId);
        if (app) {
          setLinks({ website: app.website || '', instagram: app.instagram || '', facebook: app.facebook || '', tiktok: app.tiktok || '' });
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); setLoaded(true); }
    };
    fetch();
  }, [activeVenueAppId]);

  // Auto-save on every edit (debounced 500ms). Skips the first render
  // after load so we don't POST unchanged data.
  useEffect(() => {
    latestLinksRef.current = links;
    if (!loaded) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await saveSelectedVenueApplication({ ...latestLinksRef.current, step: 4, submitted: false }, activeVenueAppId);
      } catch (err) {
        toast.error('Couldn’t save — check your connection');
      }
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [links, loaded, activeVenueAppId]);

  // Flush any pending debounced save immediately. Used by the back
  // button so edits made in the last 500ms aren't dropped when the
  // user leaves the page.
  const flushPendingSave = async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      try {
        await saveSelectedVenueApplication({ ...latestLinksRef.current, step: 4, submitted: false }, activeVenueAppId);
      } catch { /* silent — user is leaving */ }
    }
  };

  const fields = [
    { key: 'website', label: 'Website' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'tiktok', label: 'Tiktok' },
  ];

  return (
    <div className="app-shell" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40 }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <VenueSetupBackButton testId="venue-social-back" onBeforeNavigate={flushPendingSave} />
      </div>
      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-social-media-shared-logo" />
      </div>
      <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8 }}>Social Media</h2>
      <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', textAlign: 'center', margin: '8px 24px 24px', lineHeight: 1.4 }}>
        Add the venues' Website and Social Media Links for promotional purposes.
      </p>

      <div style={{ width: '100%', maxWidth: 340, padding: '0 16px' }}>
        {fields.map(f => (
          <div key={f.key} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontFamily: font, fontSize: 20, color: 'var(--text-3)' }}>{f.label}</label>
              {links[f.key] && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Provided</span>
                  <CheckCircle size={20} color="var(--status-success-text)" />
                </div>
              )}
            </div>
            <input
              data-testid={`venue-social-input-${f.key}`}
              value={links[f.key]}
              onChange={e => setLinks(prev => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={`Enter ${f.label.toLowerCase()} URL...`}
              style={{ width: '100%', fontFamily: font, fontSize: 16, color: 'var(--text)', padding: '6px 0', border: 'none', borderBottom: '2px dotted var(--card-border)', outline: 'none', background: 'transparent', boxSizing: 'border-box' }}
            />
          </div>
        ))}
      </div>

      <button onClick={() => {}} data-testid="venue-social-done-button" style={{ display: 'none' }}>DONE</button>
    </div>
  );
}
